// Catalog Drag-and-Drop Manager
// Handles desktop mouse drag and mobile touch drag with long-press

import { debugConsole } from './debug';

export class CatalogDragDropManager {
  private container: HTMLElement;
  private onOrderChange: (newOrder: string[]) => void;
  private draggedElement: HTMLElement | null = null;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private longPressTimer: number | null = null;
  private isDragging: boolean = false;

  constructor(container: HTMLElement, onOrderChange: (newOrder: string[]) => void) {
    this.container = container;
    this.onOrderChange = onOrderChange;
    this.setupDesktopDrag();
    this.setupTouchDrag();
    debugConsole.log('[DragDrop] Manager initialized');
  }

  destroy(): void {
    debugConsole.log('[DragDrop] Manager destroyed');
  }

  private setupDesktopDrag(): void {
    // Dragstart - set dragged element and add visual feedback
    this.container.addEventListener('dragstart', this.handleDragStart.bind(this));

    // Dragend - clean up and sync order
    this.container.addEventListener('dragend', this.handleDragEnd.bind(this));

    // Dragover - calculate drop position and reorder
    this.container.addEventListener('dragover', this.handleDragOver.bind(this));

    debugConsole.log('[DragDrop] Desktop drag listeners attached');
  }

  private handleDragStart(e: DragEvent): void {
    const target = e.target as HTMLElement;
    if (target.classList.contains('catalog-item')) {
      this.draggedElement = target;
      target.classList.add('dragging');
      const catalogId = target.getAttribute('data-catalog-id');
      debugConsole.log(`[DragDrop] Drag started: ${catalogId}`);
    }
  }

  private handleDragEnd(e: DragEvent): void {
    const target = e.target as HTMLElement;
    if (target.classList.contains('catalog-item')) {
      target.classList.remove('dragging');
      this.syncConfigOrder();
      this.draggedElement = null;
      debugConsole.log('[DragDrop] Drag ended');
    }
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();

    const target = (e.target as HTMLElement).closest('.catalog-item') as HTMLElement;
    if (target && this.draggedElement && target !== this.draggedElement) {
      const rect = target.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      // Remove previous drop zone indicators
      this.container.querySelectorAll('.drag-over-top, .drag-over-bottom').forEach(el => {
        el.classList.remove('drag-over-top', 'drag-over-bottom');
      });

      if (e.clientY < midpoint) {
        target.classList.add('drag-over-top');
        this.container.insertBefore(this.draggedElement, target);
      } else {
        target.classList.add('drag-over-bottom');
        this.container.insertBefore(this.draggedElement, target.nextSibling);
      }
    }
  }

  private syncConfigOrder(): void {
    const catalogItems = this.container.querySelectorAll('.catalog-item');
    const newOrder: string[] = [];

    catalogItems.forEach(item => {
      const catalogId = item.getAttribute('data-catalog-id');
      if (catalogId) {
        newOrder.push(catalogId);
      }
    });

    debugConsole.log(`[DragDrop] New order: ${newOrder.join(', ')}`);
    this.onOrderChange(newOrder);
  }

  private setupTouchDrag(): void {
    // Listen for touch events on drag handles
    this.container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.container.addEventListener('touchend', this.handleTouchEnd.bind(this));
    this.container.addEventListener('touchcancel', this.handleTouchCancel.bind(this));

    debugConsole.log('[DragDrop] Touch drag listeners attached');
  }

  private handleTouchStart(e: TouchEvent): void {
    const target = e.target as HTMLElement;
    const dragHandle = target.closest('.drag-handle');

    if (!dragHandle) return;

    const catalogItem = dragHandle.closest('.catalog-item') as HTMLElement;
    if (!catalogItem) return;

    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.draggedElement = catalogItem;

    // Start long-press timer (800ms)
    this.longPressTimer = window.setTimeout(() => {
      this.enterDragMode();
    }, 800);

    const catalogId = catalogItem.getAttribute('data-catalog-id');
    debugConsole.log(`[DragDrop] Touch start on ${catalogId}`);
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.draggedElement) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    // If user moved >10px during long-press timer, cancel drag (they're scrolling)
    if (!this.isDragging && (deltaX > 10 || deltaY > 10)) {
      this.cancelLongPress();
      return;
    }

    // If in drag mode, prevent scrolling and handle drag
    if (this.isDragging) {
      e.preventDefault();

      // Find element under touch point
      const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
      const targetCatalog = elementUnderTouch?.closest('.catalog-item') as HTMLElement;

      if (targetCatalog && targetCatalog !== this.draggedElement) {
        // Remove previous indicators
        this.container.querySelectorAll('.drag-over-top, .drag-over-bottom').forEach(el => {
          el.classList.remove('drag-over-top', 'drag-over-bottom');
        });

        // Calculate drop position
        const rect = targetCatalog.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;

        if (touch.clientY < midpoint) {
          targetCatalog.classList.add('drag-over-top');
          this.container.insertBefore(this.draggedElement, targetCatalog);
        } else {
          targetCatalog.classList.add('drag-over-bottom');
          this.container.insertBefore(this.draggedElement, targetCatalog.nextSibling);
        }
      }
    }
  }

  private handleTouchEnd(_e: TouchEvent): void {
    if (this.isDragging && this.draggedElement) {
      this.exitDragMode();
      this.syncConfigOrder();
    }

    this.cancelLongPress();
    this.draggedElement = null;
    debugConsole.log('[DragDrop] Touch ended');
  }

  private handleTouchCancel(_e: TouchEvent): void {
    this.cancelLongPress();
    if (this.isDragging && this.draggedElement) {
      this.exitDragMode();
    }
    this.draggedElement = null;
    debugConsole.log('[DragDrop] Touch cancelled');
  }

  private enterDragMode(): void {
    if (!this.draggedElement) return;

    this.isDragging = true;
    this.draggedElement.classList.add('dragging');

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }

    const catalogId = this.draggedElement.getAttribute('data-catalog-id');
    debugConsole.log(`[DragDrop] Drag mode activated for ${catalogId}`);
  }

  private exitDragMode(): void {
    if (!this.draggedElement) return;

    this.isDragging = false;
    this.draggedElement.classList.remove('dragging');

    // Remove drop zone indicators
    this.container.querySelectorAll('.drag-over-top, .drag-over-bottom').forEach(el => {
      el.classList.remove('drag-over-top', 'drag-over-bottom');
    });

    debugConsole.log('[DragDrop] Drag mode exited');
  }

  private cancelLongPress(): void {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }
}
